import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import api from "../../../core/services/api";
import { getCurrentUser, getUserRole, isSuperAdmin, isAdmin, isReseller } from "../../../core/utils/auth";
import { CheckIcon } from "@heroicons/react/24/outline";

// Icons
const ArrowLeftIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
  </svg>
);

const WhatsappEmbeddedSignup = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userRole = getUserRole(currentUser);
  const isPrivileged = isSuperAdmin(currentUser) || isAdmin(currentUser) || isReseller(currentUser);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedOnboardingType, setSelectedOnboardingType] = useState("whatsapp_business_onboarding");
  const [isProcessing, setIsProcessing] = useState(false);
  const [fbSdkLoaded, setFbSdkLoaded] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [capturedSession, setCapturedSession] = useState(null);

  // Meta configuration from env
  const appId = process.env.REACT_APP_META_APP_ID || "1099706382261998";
  const graphVersion = process.env.REACT_APP_META_GRAPH_VERSION || "v21.0";

  const configIds = {
    whatsapp_business_onboarding:
      process.env.REACT_APP_META_CONFIG_ID_WABA_ONBOARDING || "1627326771702993",
    coexistence_mode:
      process.env.REACT_APP_META_CONFIG_ID_COEXISTENCE || "1627326771702993",
    waba_only_sharing:
      process.env.REACT_APP_META_CONFIG_ID_WABA_SHARING || "1627326771702993",
  };

  const apiCall = async (endpoint, options = {}) => {
    const method = (options.method || "GET").toUpperCase();
    if (method === "POST") {
      return await api.post(endpoint, options.body);
    } else if (method === "PUT") {
      return await api.put(endpoint, options.body);
    } else {
      return await api.get(endpoint, { params: options.params });
    }
  };

  useEffect(() => {
    fetchUsers();
    loadFbSdk();

    // Message listener for FB Login popup
    const messageListener = (event) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      )
        return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data && (data.type === "WA_EMBEDDED_SIGNUP" || data.event === "FINISH")) {
          console.log("Embedded Signup Event Captured:", data);
          setCapturedSession(data);
        }
      } catch (e) {
        // Not a JSON message, ignore
      }
    };

    window.addEventListener("message", messageListener);
    return () => window.removeEventListener("message", messageListener);
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await apiCall("/whatsapp-embedded/onboarding-list");
      if (data && data.success && Array.isArray(data.users)) {
        setUsers(data.users);
        if (data.users.length > 0) {
          // If currentUser is in the list, default to currentUser, else first item
          const match = data.users.find(
            (u) =>
              String(u._id) === String(currentUser?._id) ||
              String(u.username) === String(currentUser?.str_username)
          );
          setSelectedUser(match ? match._id : data.users[0]._id);
        }
      }
    } catch (error) {
      console.error("Failed to load users:", error);
      // Fallback with current user if regular user
      if (currentUser) {
        const fallback = [
          {
            _id: currentUser._id || currentUser.obj__id,
            name: currentUser.str_name || currentUser.str_username,
            email: currentUser.str_email || "",
            bool_embedded_signup_enabled: currentUser.bool_embedded_signup_enabled,
            int_embedded_signup_limit: currentUser.int_embedded_signup_limit,
            int_embedded_signup_used: currentUser.int_embedded_signup_used,
          },
        ];
        setUsers(fallback);
        setSelectedUser(fallback[0]._id);
      }
    }
  };

  const loadFbSdk = () => {
    if (document.getElementById("facebook-jssdk")) {
      setFbSdkLoaded(true);
      return;
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: graphVersion,
      });
      setFbSdkLoaded(true);
    };

    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      js = d.createElement(s);
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    })(document, "script", "facebook-jssdk");
  };

  const handleConnectClick = async () => {
    if (!selectedUser) {
      toast.error("Please select a user first");
      return;
    }

    const currentSelected = users.find((u) => String(u._id) === String(selectedUser));
    if (!isPrivileged && currentSelected) {
      if (!currentSelected.bool_embedded_signup_enabled) {
        Swal.fire({
          icon: "warning",
          title: "Embedded Signup Disabled",
          text: "Embedded signup is currently disabled for your account. Please contact your administrator.",
        });
        return;
      }
      if (
        currentSelected.int_embedded_signup_limit > 0 &&
        currentSelected.int_embedded_signup_used >= currentSelected.int_embedded_signup_limit
      ) {
        Swal.fire({
          icon: "info",
          title: "Limit Reached",
          text: `You have reached your allowed Embedded Signup quota (${currentSelected.int_embedded_signup_used}/${currentSelected.int_embedded_signup_limit}). Please contact administrator.`,
        });
        return;
      }
    }

    if (!appId || !configIds[selectedOnboardingType]) {
      toast.error("Meta configuration missing for selected onboarding type.");
      return;
    }

    setIsProcessing(true);
    setSuccessData(null);

    try {
      // 1. Start session on backend
      await apiCall("/whatsapp-embedded/start", {
        method: "POST",
        body: {
          userId: selectedUser,
          onboardingType: selectedOnboardingType,
        },
      });

      // Prepare extras payload according to Meta Embedded Signup v3 specifications
      const extras = {
        setup: {
          version: "v3",
        },
        sessionInfoVersion: "3",
        session_info_version: "3",
        "session info version": "3",
        "es-version": "v3",
      };

      if (selectedOnboardingType === "coexistence_mode") {
        extras.setup = {
          setup_mode: "COEXISTENCE",
          version: "v3",
        };
        extras.featureType = "whatsapp_business_app_onboarding";
        extras.feature_type = "whatsapp_business_app_onboarding";
        extras["Feature Type"] = "whatsapp_business_app_onboarding";
        extras.features = ["marketing_messages_lite", "app_only_install"];
        extras.sessionInfoVersion = "3";
        extras.session_info_version = "3";
        extras["session info version"] = "3";
        extras["es-version"] = "v3";
      }

      if (!window.FB) {
        setIsProcessing(false);
        toast.error("Facebook SDK is not ready yet. Please refresh the page.");
        return;
      }

      // 2. Launch FB Login
      window.FB.login(
        (response) => {
          if (response.authResponse && response.authResponse.code) {
            handleFbCallback(response.authResponse.code);
          } else {
            setIsProcessing(false);
            toast.error("Facebook login cancelled or failed");
          }
        },
        {
          config_id: configIds[selectedOnboardingType],
          response_type: "code",
          override_default_response_type: true,
          extras: extras,
        }
      );
    } catch (error) {
      setIsProcessing(false);
      toast.error(error.message || "Failed to start onboarding process");
      console.error(error);
    }
  };

  const handleFbCallback = async (code) => {
    try {
      toast.loading("Processing WhatsApp connection & provisioning numbers...", {
        id: "wa-onboarding",
      });

      const data = await apiCall("/whatsapp-embedded/callback", {
        method: "POST",
        body: {
          userId: selectedUser,
          onboardingType: selectedOnboardingType,
          authCode: code,
          sessionInfo: capturedSession || {},
        },
      });

      if (data && data.success) {
        toast.success("WhatsApp connected and provisioned successfully!", {
          id: "wa-onboarding",
        });
        setSuccessData(data.details);
        // Refresh users list to update quota counters
        fetchUsers();
      } else {
        toast.error((data && data.error) || "Failed to connect WhatsApp", {
          id: "wa-onboarding",
        });
      }
    } catch (error) {
      toast.error(error.message || "Error processing WhatsApp connection", {
        id: "wa-onboarding",
      });
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedUserData = users.find((u) => String(u._id) === String(selectedUser));

  return (
    <div className="space-y-6 mx-auto pb-12 font-sans">
      {/* Top Navigation & Back Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(isPrivileged ? "/admin/phone-numbers" : "/phone-numbers")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors cursor-pointer"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Phone Numbers
        </button>

        {selectedUserData && !isPrivileged && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Allowed Limit:{" "}
            <span className="font-bold text-gray-800 dark:text-gray-200">
              {selectedUserData.int_embedded_signup_used} / {selectedUserData.int_embedded_signup_limit || "Unlimited"}
            </span>
          </div>
        )}
      </div>

      <div className="w-full mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header Section */}
        <div className="p-6 sm:p-8 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow-sm shadow-blue-500/30">
              1
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Step 1: Select Onboarding Type
            </h1>
          </div>
          <p className="text-gray-500 dark:text-gray-400 ml-11 text-sm sm:text-base">
            Choose how you want to connect WhatsApp.
          </p>
        </div>

        {/* User Selection */}
        <div className="p-6 sm:p-8 pt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Connect For User
          </label>
          <div className="max-w-md">
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              disabled={isProcessing || (!isPrivileged && users.length <= 1)}
            >
              {users.length === 0 && <option value="">Loading eligible users...</option>}
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.email || user.username} ({user.name || user.username})
                </option>
              ))}
            </select>
            {selectedUserData && !isPrivileged && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Quota: {selectedUserData.int_embedded_signup_used} of {selectedUserData.int_embedded_signup_limit || 0} used
                {!selectedUserData.bool_embedded_signup_enabled && (
                  <span className="text-rose-500 font-semibold ml-2">(Disabled by admin)</span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Success Card */}
        {successData && (
          <div className="px-6 sm:px-8 mb-6">
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">
                  ✓
                </div>
                <h3 className="text-emerald-900 dark:text-emerald-200 font-bold text-base">
                  Connected & Provisioned Successfully!
                </h3>
              </div>
              <p className="text-xs text-emerald-800 dark:text-emerald-300 mb-4">
                The provisioned WhatsApp details have been automatically registered in your Phone Numbers table (`tbl_phone_numbers`).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-emerald-100 dark:border-emerald-900/40 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">WABA ID</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {successData.wabaId || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">Phone Number ID</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {successData.phoneNumberId || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">Business ID</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {successData.businessId || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">Sending Phone Number</span>
                  <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                    {successData.phoneNumber || "N/A"}
                  </span>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <button
                  className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
                  onClick={() => navigate(isPrivileged ? "/admin/phone-numbers" : "/phone-numbers")}
                >
                  View in Phone Numbers List →
                </button>
                <button
                  className="text-xs text-emerald-800 dark:text-emerald-300 underline font-medium hover:text-emerald-900"
                  onClick={() => setSuccessData(null)}
                >
                  Connect another account
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onboarding Cards Grid */}
        <div className="px-6 sm:px-8 pb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card A: Business Onboarding */}
          <div
            className={`flex flex-col border rounded-2xl p-6 cursor-pointer transition-all ${
              selectedOnboardingType === "whatsapp_business_onboarding"
                ? "border-blue-500 bg-blue-50/20 dark:bg-blue-950/20 shadow-md ring-2 ring-blue-500"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
            }`}
            onClick={() => setSelectedOnboardingType("whatsapp_business_onboarding")}
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-500 text-white flex items-center justify-center font-bold text-base mb-4 shadow-sm shadow-emerald-500/20">
              WA
            </div>
            <div className="flex justify-between items-start mb-2 gap-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-lg leading-snug">
                WhatsApp Business Onboarding
              </h3>
              <span className="bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap">
                Recommended
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 min-h-[40px]">
              Create a new WhatsApp Business Account with a new phone number
            </p>

            <div className="mb-6 flex-1">
              <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                Features:
              </p>
              <ul className="space-y-2.5 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> WhatsApp Cloud API
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Marketing Messages Lite
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Templates
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Webhooks
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> New Phone Number Setup
                </li>
              </ul>
            </div>

            <button
              className={`w-full py-2.5 rounded-xl font-semibold text-white transition-all cursor-pointer shadow-sm ${
                selectedOnboardingType === "whatsapp_business_onboarding"
                  ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20"
                  : "bg-emerald-500 opacity-60"
              }`}
              onClick={handleConnectClick}
              disabled={isProcessing || selectedOnboardingType !== "whatsapp_business_onboarding"}
            >
              {isProcessing && selectedOnboardingType === "whatsapp_business_onboarding"
                ? "Connecting..."
                : "Connect WhatsApp"}
            </button>
          </div>

          {/* Card B: Coexistence Mode */}
          <div
            className={`flex flex-col border rounded-2xl p-6 cursor-pointer transition-all ${
              selectedOnboardingType === "coexistence_mode"
                ? "border-blue-500 bg-blue-50/20 dark:bg-blue-950/20 shadow-md ring-2 ring-blue-500"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
            }`}
            onClick={() => setSelectedOnboardingType("coexistence_mode")}
          >
            <div className="w-10 h-10 rounded-lg bg-blue-500 text-white flex items-center justify-center font-bold text-base mb-4 shadow-sm shadow-blue-500/20">
              CO
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-2">
              Coexistence Mode
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 min-h-[40px]">
              Connect existing WhatsApp Business App number
            </p>

            <div className="mb-6 flex-1">
              <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                Features &amp; Spec (v3):
              </p>
              <ul className="space-y-2.5 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Embedded Signup v3
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Session Info Version: 3
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Marketing Messages Lite
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> App Only Install
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> WA Business App Onboarding
                </li>
              </ul>
            </div>

            <button
              className={`w-full py-2.5 rounded-xl font-semibold transition-all mt-auto cursor-pointer ${
                selectedOnboardingType === "coexistence_mode"
                  ? "bg-white dark:bg-gray-800 text-blue-600 border-2 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                  : "bg-white dark:bg-gray-800 text-gray-500 border border-gray-300 dark:border-gray-600"
              }`}
              onClick={handleConnectClick}
              disabled={isProcessing || selectedOnboardingType !== "coexistence_mode"}
            >
              {isProcessing && selectedOnboardingType === "coexistence_mode"
                ? "Connecting..."
                : "Connect Existing Number"}
            </button>
          </div>

          {/* Card C: WABA Only Sharing */}
          <div
            className={`flex flex-col border rounded-2xl p-6 cursor-pointer transition-all ${
              selectedOnboardingType === "waba_only_sharing"
                ? "border-blue-500 bg-blue-50/20 dark:bg-blue-950/20 shadow-md ring-2 ring-blue-500"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
            }`}
            onClick={() => setSelectedOnboardingType("waba_only_sharing")}
          >
            <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center font-bold text-base mb-4 shadow-sm shadow-orange-500/20">
              WB
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-2">
              WABA Only Sharing
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 min-h-[40px]">
              Share only WhatsApp Business Account
            </p>

            <div className="mb-6 flex-1">
              <p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                Features:
              </p>
              <ul className="space-y-2.5 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> WABA Access
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> No Phone Number Setup
                </li>
                <li className="flex items-center gap-2">
                  <CheckIcon className="w-4 h-4 text-emerald-500 font-bold shrink-0" /> Business Asset Sharing
                </li>
              </ul>
            </div>

            <button
              className={`w-full py-2.5 rounded-xl font-semibold transition-all mt-auto cursor-pointer ${
                selectedOnboardingType === "waba_only_sharing"
                  ? "bg-white dark:bg-gray-800 text-orange-600 border-2 border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/40"
                  : "bg-white dark:bg-gray-800 text-gray-500 border border-gray-300 dark:border-gray-600"
              }`}
              onClick={handleConnectClick}
              disabled={isProcessing || selectedOnboardingType !== "waba_only_sharing"}
            >
              {isProcessing && selectedOnboardingType === "waba_only_sharing"
                ? "Connecting..."
                : "Share WABA"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsappEmbeddedSignup;
